/* eslint-disable no-unused-vars */

import {
  Button,
  Card,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import React, { FC, memo, useEffect, useMemo, useState } from 'react';
import { DatePickerRangeComponent } from 'src/components';
import { useRmaInventory } from 'src/hooks/rma-inventory/use-rma-inventory';
import { useSupplier } from 'src/hooks/supplier';
import useUserInfo from 'src/hooks/user/use-user-info';
import useResolution from 'src/hooks/useResolution';
import { rmaInventoryLocationOptions } from 'src/redux/slices/rma/constants/rma-form-fields';
import { branchName, toUtcEquivalentDate } from 'src/utils';

const Component: FC = () => {
  const {
    filters,
    dynamicInventoryStatusOptions,
    isLoading,
    onChangeFilters,
    getRmaInventory
  } = useRmaInventory();
  const { isMobile } = useResolution();
  const { suppliers, getSuppliers } = useSupplier();
  const { userBranches } = useUserInfo();

  // local state for temp ui changes
  const [searchBy, setSearchBy] = useState<string>(
    filters.search_by ?? 'current'
  );

  const [serialNo, setSerialNo] = useState<string>('');
  const [productName, setProductName] = useState<string>('');
  const [customerName, setCustomerName] = useState<string>('');
  const [location, setLocation] = useState<string>('');
  const [transactionNo, setTransactionNo] = useState<string>('');
  const [status, setStatus] = useState<string[]>([]);

  const [supplierId, setSupplierId] = useState<number | null>(null);
  const [dateFrom, setDateFrom] = useState<string | null>(null);
  const [dateTo, setDateTo] = useState<string | null>(null);

  const searchByOptions = useMemo(
    () => [
      { name: 'Current', value: 'current' },
      { name: 'Replaced', value: 'replaced' }
    ],
    []
  );

  const resetFilter = () => {
    setSearchBy('current');
    setSerialNo('');
    setProductName('');
    setTransactionNo('');
    setCustomerName('');
    setLocation('');
    setStatus([]);
    setSupplierId(null);

    setDateFrom(null);
    setDateTo(null);
  };

  const applyFilter = () => {
    onChangeFilters({
      search_by: searchBy ?? null,
      serial_no: serialNo.length ? serialNo : null,
      product_name: productName.length ? productName : null,
      transaction_no: transactionNo.length ? transactionNo : null,
      customer_name: customerName.length ? productName : null,
      location: location.length ? location : null,
      status: status.length ? status : null,
      supplier_id: supplierId ?? null,
      date_from: dateFrom ?? null,
      date_to: dateTo ?? null,
      page: 1
    });
  };

  const onChangedDate = (date: MaterialUiPickersDate, field: string) => {
    const dateUtc = toUtcEquivalentDate(date)?.toISOString() ?? null;
    if (field === 'from') {
      setDateFrom(dateUtc);
      setDateTo(null); // clear to date to avoid edge case
    } else {
      setDateTo(dateUtc);
    }
  };

  const dynamicInventoryLocationOptions = useMemo(() => {
    const userBranchOptions =
      userBranches?.map((b) => branchName(b.branch_name)) || [];

    return [...userBranchOptions, ...rmaInventoryLocationOptions];
  }, [userBranches]);

  // fetch when filters have been applied
  useEffect(() => {
    getRmaInventory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // mark effect
  useEffect(() => {
    if (!suppliers?.[0]) {
      getSuppliers({
        keyword: '',
        limit: 9999
      });
    }
  }, [getSuppliers, suppliers]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1em'
      }}
    >
      <Typography color="textPrimary" variant="h3">
        RMA Inventory
      </Typography>

      <Card
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1em',
          padding: '1em'
        }}
      >
        <Grid className="searchBars" container spacing={2}>
          <Grid item xs={12}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Search by</FormLabel>
              <RadioGroup
                row
                value={searchBy}
                onChange={(_, value) => setSearchBy(value)}
              >
                {searchByOptions?.map((options, index) => (
                  <FormControlLabel
                    key={index}
                    value={options.value}
                    control={<Radio />}
                    label={`${options.name}`}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              value={serialNo}
              onChange={(e) => setSerialNo(e?.target?.value ?? '')}
              onKeyDown={(e: any) => {
                if (e.key === 'Enter' && !isLoading) {
                  applyFilter();
                }
              }}
              label="Serial No."
              variant="outlined"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              value={productName}
              onChange={(e) => setProductName(e?.target?.value ?? '')}
              onKeyDown={(e: any) => {
                if (e.key === 'Enter' && !isLoading) {
                  applyFilter();
                }
              }}
              label="Product Name"
              variant="outlined"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              value={transactionNo}
              onKeyDown={(e: any) => {
                if (e.key === 'Enter' && !isLoading) {
                  applyFilter();
                }
              }}
              onChange={(e) => setTransactionNo(e?.target?.value ?? '')}
              label="Transaction No."
              variant="outlined"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              value={customerName}
              onKeyDown={(e: any) => {
                if (e.key === 'Enter' && !isLoading) {
                  applyFilter();
                }
              }}
              onChange={(e) => setCustomerName(e?.target?.value ?? '')}
              label="Customer Name"
              variant="outlined"
              fullWidth
            />
          </Grid>
        </Grid>

        <Grid className="dropDowns" container justify="center" spacing={2}>
          <Grid item xs={12} sm={3}>
            <Autocomplete
              id="supplier_id"
              options={suppliers}
              fullWidth
              getOptionLabel={(option) => option.name || ''}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Supplier"
                  variant="outlined"
                  autoComplete="off"
                />
              )}
              onChange={(_, val) => {
                setSupplierId(val?.id || null);
              }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <Autocomplete
              options={dynamicInventoryLocationOptions}
              onChange={(_, value) => setLocation(value ?? '')}
              renderInput={(params) => (
                <TextField {...params} label={'Location'} variant="outlined" />
              )}
            />
          </Grid>
          {/* todo: change to generic dropdown in the future */}
          <Grid item xs={12} sm={3}>
            <Autocomplete
              style={{ background: 'white' }}
              id="options-dropdown"
              options={dynamicInventoryStatusOptions}
              value={status}
              limitTags={2}
              fullWidth
              multiple
              disableCloseOnSelect
              onChange={(event, newValue) => setStatus(newValue || [])}
              getOptionLabel={(option) => option}
              renderOption={(option, { selected }) => {
                return (
                  <>
                    <Checkbox checked={selected} style={{ marginRight: 8 }} />
                    {option}
                  </>
                );
              }}
              renderTags={(value: any[]) => (
                <div
                  style={{
                    display: 'block',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    lineHeight: '1.5em'
                  }}
                >
                  {status.length == dynamicInventoryStatusOptions.length
                    ? 'All'
                    : value
                        .slice()
                        .map((v) => v)
                        .join(', ')}
                </div>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={'Status'}
                  variant="outlined"
                  InputProps={{
                    readOnly: true,
                    ...params.InputProps,
                    style: { flexWrap: 'nowrap' },
                    inputProps: {
                      ...params.inputProps,
                      style: {
                        caretColor: 'black'
                      }
                    }
                  }}
                />
              )}
            />
          </Grid>

          <Grid
            item
            xs={12}
            style={{
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <DatePickerRangeComponent
              title="Date Sold (Customer)"
              toDateValue={dateTo}
              fromDateValue={dateFrom}
              toDateMin={dateFrom}
              dateToLabel={isMobile ? 'To' : 'Date Sold (To)'}
              dateFromLabel={isMobile ? 'From' : 'Date Sold (From)'}
              onChangeToDate={(date) => onChangedDate(date, 'to')}
              onChangeFromDate={(date) => onChangedDate(date, 'from')}
            />
          </Grid>
        </Grid>

        <Grid className="buttons" container justify="space-between" spacing={2}>
          <Grid item xs={12} sm={2}>
            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              style={{ height: '100%' }}
              onClick={resetFilter}
            >
              reset filters
            </Button>
          </Grid>

          <Grid item xs={12} sm={2}>
            <Button
              disabled={isLoading}
              variant="contained"
              color="primary"
              fullWidth
              style={{ height: '100%' }}
              onClick={applyFilter}
            >
              apply filter
            </Button>
          </Grid>
        </Grid>
      </Card>
    </div>
  );
};

export const RmaToolbar = memo(Component);
